import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Home, Calculator as CalcIcon, ShoppingCart, Briefcase, LogOut, ShoppingBag, Wrench, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import AuthModal from './AuthModal';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { getTotalItems } = useCart();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const cartItemCount = getTotalItems();

  const navLinks = [
    { to: '/calculator', icon: CalcIcon, label: 'Calculator', testId: 'nav-calculator' },
    { to: '/magazin', icon: ShoppingCart, label: 'Magazin', testId: 'nav-shop' },
    { to: '/servicii', icon: Wrench, label: 'Servicii', testId: 'nav-services' },
    { to: '/portofoliu', icon: Briefcase, label: 'Portofoliu', testId: 'nav-portfolio' },
  ];

  return (
    <nav data-testid="main-navbar" className="glass-navbar fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 flex-shrink-0" data-testid="nav-logo">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-lime-500 rounded-xl flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div className="hidden xs:block">
              <div className="text-xl font-bold text-cyan-600" style={{ fontFamily: 'Outfit, sans-serif' }}>IzoMAG</div>
              <div className="text-xs text-slate-600 hidden sm:block">Soluția corectă la prețul corect</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link 
                key={link.to}
                to={link.to} 
                className="px-4 py-2 rounded-full text-slate-700 hover:bg-white/50 transition flex items-center space-x-2" 
                data-testid={link.testId}
              >
                <link.icon className="w-4 h-4" />
                <span>{link.label}</span>
              </Link>
            ))}
          </div>

          {/* Right side: Cart, User, Mobile Menu */}
          <div className="flex items-center space-x-2 sm:space-x-3">
            {/* Cart Icon */}
            <Link to="/cos" className="relative p-2 rounded-full hover:bg-white/50 transition" data-testid="nav-cart-btn">
              <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6 text-slate-700" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-badge">
                  {cartItemCount > 99 ? '99+' : cartItemCount}
                </span>
              )}
            </Link>
            
            {/* User section - Desktop */}
            <div className="hidden sm:flex items-center space-x-2">
              {user ? (
                <>
                  <span className="text-sm text-slate-700 truncate max-w-[120px]" data-testid="nav-user-email">{user.email}</span>
                  {user.is_admin && (
                    <Link to="/admin" className="btn-secondary py-2 px-3 text-sm" data-testid="nav-admin-link">
                      Admin
                    </Link>
                  )}
                  <button onClick={logout} className="p-2 rounded-full hover:bg-white/50 transition" data-testid="nav-logout-btn">
                    <LogOut className="w-5 h-5 text-slate-700" />
                  </button>
                </>
              ) : (
                <button onClick={() => setShowAuthModal(true)} className="btn-primary py-2 px-4 text-sm" data-testid="nav-login-btn">
                  Login
                </button>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 rounded-full hover:bg-white/50 transition"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6 text-slate-700" /> : <Menu className="w-6 h-6 text-slate-700" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-200/50">
            <div className="flex flex-col space-y-1">
              {navLinks.map((link) => (
                <Link 
                  key={link.to}
                  to={link.to} 
                  className="px-4 py-3 rounded-lg text-slate-700 hover:bg-white/50 transition flex items-center space-x-3"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-${link.testId}`}
                >
                  <link.icon className="w-5 h-5" />
                  <span className="font-medium">{link.label}</span>
                </Link>
              ))}
              
              {/* Mobile User Section */}
              <div className="border-t border-slate-200/50 mt-2 pt-3">
                {user ? (
                  <div className="space-y-2">
                    <div className="px-4 py-2 text-sm text-slate-600 truncate">{user.email}</div>
                    {user.is_admin && (
                      <Link 
                        to="/admin" 
                        className="block px-4 py-3 rounded-lg text-cyan-600 hover:bg-cyan-50 transition font-medium"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Panel Admin
                      </Link>
                    )}
                    <button 
                      onClick={() => { logout(); setMobileMenuOpen(false); }} 
                      className="w-full px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition text-left flex items-center space-x-3"
                    >
                      <LogOut className="w-5 h-5" />
                      <span>Deconectare</span>
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }} 
                    className="w-full btn-primary py-3 text-center"
                  >
                    Login
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </nav>
  );
};

export default Navbar;
